import { Bell, Link2 } from 'lucide-react';
import { useState } from 'react';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { cn } from '@/lib/utils';

type Platform = 'android' | 'ios';

/**
 * Approximates how the system shows the notification on a lock screen. The OS decides the real
 * look (font, truncation, grouping), so this is a guide for length and wording, not a pixel match.
 */
export function NotificationPreview({ appName, title, body, actionUrl, data }: { appName: string; title: string; body: string; actionUrl: string; data: string }) {
  const [platform, setPlatform] = useState<Platform>('android');
  const shownTitle = title.trim() || 'Title';
  const dataKeys = countKeys(data);

  return (
    <div className="space-y-3">
      <Tabs value={platform} onValueChange={(v) => setPlatform(v as Platform)}>
        <TabsList className="w-full">
          <TabsTrigger value="android">Android</TabsTrigger>
          <TabsTrigger value="ios">iOS</TabsTrigger>
        </TabsList>
      </Tabs>

      {/* Fixed "wallpaper" colours: a phone looks the same whichever theme the dashboard uses. */}
      <div className="rounded-2xl bg-gradient-to-br from-indigo-500 via-violet-500 to-sky-400 p-3 pt-8 shadow-inner">
        <p className="mb-6 text-center text-4xl font-light tracking-tight text-white/95 tabular-nums">9:41</p>
        {platform === 'android' ? (
          <div className="rounded-3xl bg-white p-4 text-neutral-900 shadow-lg">
            <div className="flex items-center gap-1.5 text-[11px] text-neutral-600">
              <Bell className="size-3 text-indigo-600" />
              <span className="truncate">{appName}</span>
              <span>· now</span>
            </div>
            <p className={cn('mt-1.5 line-clamp-1 text-sm font-semibold', !title.trim() && 'text-neutral-400')}>{shownTitle}</p>
            {body.trim() && <p className="line-clamp-2 text-[13px] text-neutral-700">{body}</p>}
          </div>
        ) : (
          <div className="flex gap-2.5 rounded-2xl bg-white/80 p-3 text-neutral-900 shadow-lg backdrop-blur-md">
            <span className="grid size-9 shrink-0 place-items-center rounded-lg bg-indigo-600 text-sm font-semibold text-white">{appName.slice(0, 1).toUpperCase()}</span>
            <div className="min-w-0 flex-1">
              <div className="flex items-baseline justify-between gap-2">
                <p className={cn('line-clamp-1 text-[13px] font-semibold', !title.trim() && 'text-neutral-400')}>{shownTitle}</p>
                <span className="shrink-0 text-[11px] text-neutral-500">now</span>
              </div>
              {body.trim() && <p className="line-clamp-4 text-[13px] leading-snug text-neutral-800">{body}</p>}
            </div>
          </div>
        )}
      </div>

      <ul className="space-y-1.5 text-xs text-muted-foreground">
        {actionUrl.trim() && (
          <li className="flex gap-1.5"><Link2 className="mt-0.5 size-3.5 shrink-0" /><span className="min-w-0 break-all">Tapping opens <span className="font-mono text-foreground">{actionUrl.trim()}</span></span></li>
        )}
        {dataKeys > 0 && <li>{dataKeys} data {dataKeys === 1 ? 'field is' : 'fields are'} delivered to the app and not shown.</li>}
        <li>Long text is cut off by the device; this preview shows roughly where.</li>
      </ul>
    </div>
  );
}

function countKeys(json: string): number {
  if (!json.trim()) return 0;
  try {
    const v: unknown = JSON.parse(json);
    return v && typeof v === 'object' && !Array.isArray(v) ? Object.keys(v).length : 0;
  } catch {
    return 0;
  }
}
